<template>
  <Dialog
    :title="`Assign Factory to Order: #${orderItemRef?.order.order_no}`"
    :showCloseButton="true"
    :showFooter="true"
    :showSaveButton="true"
    :showCancelButton="true"
    saveButtonText="Save"
    cancelButtonText="Cancel"
    :sm="true"
    @closeEvent="closeEvent"
    @cancelEvent="closeEvent"
    @saveEvent="validateFactoryAssignmentData(orderItemRef ? orderItemRef : orderItem)"
  >
    <template #modal-content>
      <div class="assign_factory p-2">
        <div
          :class="factory_product_index == 0 ? 'mt-1' : 'mt-3'"
          v-for="(factory_product, factory_product_index) in (orderItemRef
            ? orderItemRef
            : orderItem
          ).factory_products"
          :key="factory_product.id"
        >
          <h2 class="text-black text-sm font-300 text-left mb-1">
            {{
              factory_product.is_new_product
                ? factory_product.new_product_name
                : factory_product.product_name
            }}
            <template v-if="factory_product.reorder_data">
              {{
                `(Reorder from ${factory_product.reorder_data.order_number}: made at:
                ${factory_product.product_last_factory ?? 'N/A '})`
              }}
            </template>
          </h2>
          <div class="flex gap-4">
            <Select
              v-model="factory_product.selected_factory"
              :options="factories"
              label="name"
              track-by="id"
              placeholder="Select factory"
              class="flex-1"
              append-to-body
              @update:modelValue="
                (selectedFactory: any) =>
                  getFactoryAsanaTemplates(selectedFactory, factory_product, factory_product_index)
              "
            />
            <Select
              v-if="factory_product.selected_factory"
              v-model="factory_product.selected_asana_template_id"
              :options="asana_templates[factory_product.selected_factory.id] || []"
              label="name"
              track-by="id"
              placeholder="Select asana template"
              class="flex-1"
              append-to-body
              :loading="loading[factory_product_index]"
              @update:modelValue="syncAsanaTemplates(factory_product)"
            />
          </div>
        </div>

        <div class="mt-3">Assign Order Admin</div>
        <Select
          :options="filteredOrderAdmins"
          :modelValue="selectedOrderAdmin"
          track-by="id"
          label="name"
          append-to-body
          class="white-space-nowrap"
          placeholder="Assign Admin"
          @update:modelValue="setSelectedOrderAdmin"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showConfirm, showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, Factory, FactoryProduct, OrderAdminUser } from '@/models';
import { useMainStore } from '@/store/index';
import { storeToRefs } from 'pinia';

import { computed, defineProps, onMounted, onUnmounted, reactive, ref } from 'vue';
const store = useMainStore();
const { factories, orderAdmins } = storeToRefs(store);

const props = defineProps<{
  orderItem: any;
}>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'onSave', fetch: boolean): void;
  (e: 'handleOrderUpdated', orderItem: DashboardItem): void;
}>();
const loading = ref<boolean[]>([]);
const orderItemRef = ref<DashboardItem | null>(props.orderItem);
let factory_ids = ref<number[]>([]);
let selectedOrderAdmin = ref<OrderAdminUser | null>(null);

onMounted(() => {
  orderItemRef.value = props.orderItem;
});
onUnmounted(() => {
  orderItemRef.value = null;
});

const filteredOrderAdmins = computed(() => {
  const factoryIds = factory_ids.value;
  return orderAdmins.value.filter((admin) =>
    admin.factory_ids?.some((fid) => factoryIds?.includes(fid))
  );
});

const setSelectedOrderAdmin = (user: OrderAdminUser | null) => {
  selectedOrderAdmin.value = user;
};

const validateFactoryAssignmentData = async (order_item: DashboardItem) => {
  if (order_item.factory_products.every((product) => product.selected_factory !== null)) {
    const is_multiple_factories_assigned = isMultipleFactoriesAssignedToSameProduct(order_item);
    if (typeof is_multiple_factories_assigned === 'boolean') {
      const orderAdminPayload = {
        user_id: selectedOrderAdmin.value?.id,
        order_ids: order_item.order.id,
      };
      if (is_multiple_factories_assigned) {
        const confirmed = await showConfirm(
          'Are you sure!',
          'You are assigning different factories to same product'
        );
        if (confirmed) {
          await store.updateOrderAdmin(orderAdminPayload);
          await assignFactory(order_item);
        }
      } else {
        await store.updateOrderAdmin(orderAdminPayload);
        await assignFactory(order_item);
      }
    } else {
      showToast('Please select factory for all products.', 'error');
    }
  }
  else {
    showToast('Please select factory for all products.', 'error');
  }
};

const assignFactory = async (order_item: DashboardItem) => {
  const payload_object: Record<any, any> = {
    product_factory_ids: {},
    asana_template_ids: {},
  };

  order_item.factory_products.forEach((factory_product) => {
    if (factory_product.selected_factory) {
      payload_object.product_factory_ids[factory_product.id] = factory_product.selected_factory.id;

      if (factory_product.selected_asana_template_id) {
        payload_object.asana_template_ids[factory_product.selected_factory.id] =
          factory_product.selected_asana_template_id.id;
      }
    }
  });

  const payload = {
    order_item_id: order_item.id,
    order_id: order_item.order_id,
    product_factory_ids: payload_object.product_factory_ids,
    asana_template_ids: payload_object.asana_template_ids,
  };
  await store.assignFactory(payload);
  emit('onSave', true);
  emit('handleOrderUpdated', order_item);
  closeEvent();
};

const isMultipleFactoriesAssignedToSameProduct = (order_item: DashboardItem): boolean | 'error' => {
  const factory_products: FactoryProduct[] = order_item.factory_products;
  let is_multiple_factories_assigned_to_same_product = false;
  let products_factory_info: Record<number, number[]> = {};
  if (factory_products.some((factory_product) => !factory_product.selected_factory)) {
    return 'error';
  }
  factory_products.forEach((factory_product) => {
    const product_id = factory_product.product_id;
    if (products_factory_info[product_id]) {
      products_factory_info[product_id].push(factory_product.selected_factory.id);
    } else {
      products_factory_info[product_id] = [factory_product.selected_factory.id];
    }
  });

  Object.values(products_factory_info).some((factory_ids) => {
    if (new Set(factory_ids).size !== 1) {
      is_multiple_factories_assigned_to_same_product = true;
      return true;
    }
    return false;
  });

  return is_multiple_factories_assigned_to_same_product;
};

/*
  Code related to Asana templates assignment to factory products
*/

interface AsanaTemplate {
  id: number;
  name: string;
}
const asana_templates = reactive<Record<number, AsanaTemplate[]>>({});

const getFactoryAsanaTemplates = async (
  factory: Factory,
  factory_product: FactoryProduct,
  index: number
) => {
  factory_ids.value[index] = factory.id;
  if (factory_product?.selected_asana_template_id) {
    factory_product.selected_asana_template_id = null;
  }
  if (factory.asana_project_id && !asana_templates[factory.id]) {
    loading.value[index] = true;
    await http.get(`/get-asana-task-templates?factory_id=${factory.id}`).then((res) => {
      asana_templates[factory.id] = res.data.map((obj: any) => ({
        id: obj.gid,
        name: obj.template.name,
      }));
    });

    loading.value[index] = false;
  }

  applyStoredAsanaTemplate(factory_product); // Apply stored template if available
};

const closeEvent = () => {
  emit('close');
};

const factoryProducts = reactive([...props.orderItem.factory_products]);
const selectedFactoryTemplates = reactive<Record<number, AsanaTemplate | null>>({});

// Function to apply stored Asana template if available
const applyStoredAsanaTemplate = (factory_product: FactoryProduct) => {
  if (factory_product.selected_factory) {
    const factoryId = factory_product.selected_factory.id;
    if (selectedFactoryTemplates[factoryId]) {
      factory_product.selected_asana_template_id = selectedFactoryTemplates[factoryId];
    }
  }
};

// Function to update Asana templates and sync with global selection
const syncAsanaTemplates = (factory_product: FactoryProduct) => {
  if (factory_product.selected_factory) {
    selectedFactoryTemplates[factory_product.selected_factory.id] =
      factory_product.selected_asana_template_id;
  }

  factoryProducts.forEach((product: FactoryProduct) => {
    if (
      product.selected_factory &&
      factory_product.selected_factory &&
      product.selected_factory.id === factory_product.selected_factory.id
    ) {
      product.selected_asana_template_id = factory_product.selected_asana_template_id;
    }
  });
};

/*
 End Code related to Asana templates assignment to factory products
*/
</script>

<style scoped></style>
